import {
  Component, ChangeDetectionStrategy, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, Input, Output, EventEmitter, inject, DOCUMENT,
} from '@angular/core'
import { VideoIntl } from '../video.intl'
import {
  DOWN_ARROW, END, HOME, LEFT_ARROW, PAGE_DOWN, PAGE_UP, RIGHT_ARROW, UP_ARROW,
} from '@knorr-bremse-portals/ngx-components'
import { DurationPipe } from '@knorr-bremse-portals/ngx-components/pipes/duration'
import { HumanDurationPipe } from '@knorr-bremse-portals/ngx-components/pipes/human-duration'

@Component({
  selector: 'kb-seek-slider',
  templateUrl: './seek-slider.component.html',
  styleUrls: ['./seek-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DurationPipe, HumanDurationPipe],
})
export class SeekSliderComponent implements AfterViewInit {
  private readonly STEP_SECONDS_ARROW = 5
  private readonly STEP_SECONDS_PAGE = 60

  @Input()
  public set secondsMax(seconds: number) {
    this._secondsMax = seconds

    if (this.afterViewInit) {
      this.updateDimensions()
      this.updateProgressDisplay(this.secondsNow / this.secondsMax)
    }
  }

  public get secondsMax(): number {
    return this._secondsMax
  }

  private _secondsMax!: number

  @Input()
  public set secondsNow(seconds: number) {
    this._secondsNow = Math.min(Math.max(seconds, 0), this.secondsMax)

    if (this.afterViewInit) {
      this.updateDimensions()
      this.updateProgressDisplay(this.secondsNow / this.secondsMax)
    }
  }

  public get secondsNow(): number {
    return this._secondsNow
  }

  private _secondsNow!: number

  @Output() public readonly secondsNowChange = new EventEmitter<number>()

  @ViewChild('trackFull', { static: true }) private trackFullElement!: ElementRef<HTMLDivElement>
  @ViewChild('thumb', { static: true }) private thumbElement!: ElementRef<HTMLDivElement>
  @ViewChild('trackProgress', { static: true }) private trackProgressElement!: ElementRef<HTMLDivElement>

  private trackLeft!: number
  private trackWidth!: number

  private boundPointerMove = this.onPointerMove.bind(this)

  protected pointerFocus = false

  private document = inject(DOCUMENT)
  private cdr = inject(ChangeDetectorRef)
  protected intl = inject(VideoIntl)

  private afterViewInit = false

  public ngAfterViewInit(): void {
    this.updateDimensions()
    this.updateProgressDisplay(this.secondsNow / this.secondsMax)

    this.afterViewInit = true
  }

  protected onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case LEFT_ARROW:
      case DOWN_ARROW:
        this.updateSecondsNow(this.secondsNow - this.STEP_SECONDS_ARROW)
        this.keyboardEventHandled(event)
        break

      case RIGHT_ARROW:
      case UP_ARROW:
        this.updateSecondsNow(this.secondsNow + this.STEP_SECONDS_ARROW)
        this.keyboardEventHandled(event)
        break

      case PAGE_DOWN:
        this.updateSecondsNow(this.secondsNow - this.STEP_SECONDS_PAGE)
        this.keyboardEventHandled(event)
        break

      case PAGE_UP:
        this.updateSecondsNow(this.secondsNow + this.STEP_SECONDS_PAGE)
        this.keyboardEventHandled(event)
        break

      case HOME:
        this.updateSecondsNow(0)
        this.keyboardEventHandled(event)
        break

      case END:
        this.updateSecondsNow(this.secondsMax)
        this.keyboardEventHandled(event)
        break

      default:
        break
    }
  }

  private keyboardEventHandled(event: Event): void {
    this.updateDimensions()
    this.updateProgressDisplay(this.secondsNow / this.secondsMax)

    event.preventDefault()
    event.stopPropagation()
  }

  protected onClick(event: MouseEvent): void {
    // update dimensions already called on pointer down
    const progress = this.getProgressFromPointer(event)
    this.updateProgressDisplay(progress)
    this.updateProgress(progress)
  }

  protected onPointerDown(): void {
    this.updateDimensions()

    this.pointerFocus = true

    this.document.addEventListener('pointermove', this.boundPointerMove)
    this.document.addEventListener('pointerup', () => {
      this.document.removeEventListener('pointermove', this.boundPointerMove)

      this.pointerFocus = false
      this.cdr.markForCheck()
    }, { once: true })
  }

  private getProgressFromPointer(event: MouseEvent): number {
    return Math.min(Math.max((event.clientX - this.trackLeft) / this.trackWidth, 0), 1)
  }

  private onPointerMove(event: MouseEvent): void {
    const progress = this.getProgressFromPointer(event)
    this.updateProgressDisplay(progress)
    this.updateProgress(progress)
  }

  private updateProgress(progress: number): void {
    this.updateSecondsNow(this._secondsMax * progress)
    this.cdr.markForCheck()
  }

  private updateSecondsNow(seconds: number): void {
    this._secondsNow = seconds
    this.secondsNowChange.emit(seconds)
  }

  private updateDimensions(): void {
    this.trackLeft = this.trackFullElement.nativeElement.getBoundingClientRect().left
    this.trackWidth = this.trackFullElement.nativeElement.clientWidth
  }

  private updateProgressDisplay(progressPercent: number): void {
    this.thumbElement.nativeElement.style.transform = `scaleX(${Math.min(1 / progressPercent, this.trackWidth)})`
    this.trackProgressElement.nativeElement.style.transform = `scaleX(${Math.max(progressPercent, 1 / this.trackWidth)})`
  }
}
